import { useEffect, useMemo, useRef, useState } from "react";
import "./NumberAnimation.css";

/**
 * Animated number primitives.
 *
 * `NumberTicker` — slot-machine rolling digits with staggered entry, for
 * values that arrive as a stream of updates (oracle price, 24h change).
 * `AnimatedNumber` — spring-ish count-up from the previous value, for figures
 * a trader changes themselves (position size, execution price).
 *
 * Written against nothing but React and CSS. The app has no `motion` package
 * and no Tailwind config, so the shadcn/motion version of these components
 * could not drop in; the props below mirror that API so call sites read the
 * same.
 */

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Fires once when the element first enters the viewport. Returns `true`
 * immediately when `enabled` is false, so `startOnView={false}` costs nothing.
 */
const useInView = (ref, enabled) => {
  const [inView, setInView] = useState(!enabled);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled]);

  return inView;
};

/**
 * One digit slot. The column carries 0–9 and slides so the wanted digit sits
 * in the window; the slide direction is whichever way the value moved, so a
 * price ticking up rolls up.
 */
const Digit = ({ digit, duration, delay, blur, className, entering }) => {
  const [rolling, setRolling] = useState(false);
  const prev = useRef(digit);

  useEffect(() => {
    if (prev.current === digit) return undefined;
    prev.current = digit;
    if (!blur || prefersReducedMotion()) return undefined;
    setRolling(true);
    const id = setTimeout(() => setRolling(false), (duration + delay) * 1000);
    return () => clearTimeout(id);
  }, [digit, blur, duration, delay]);

  return (
    <span className={`ce-num-digit${entering ? " ce-num-enter" : ""}${className ? ` ${className}` : ""}`} style={entering ? { animationDelay: `${delay}s` } : undefined}>
      <span
        className={`ce-num-col${rolling ? " is-blurred" : ""}`}
        style={{
          transform: `translateY(${-digit * 10}%)`,
          transitionDuration: `${duration}s`,
          transitionDelay: `${delay}s`,
        }}
      >
        {DIGITS.map((d) => (
          <span key={d} className="ce-num-cell">
            {d}
          </span>
        ))}
      </span>
    </span>
  );
};

/**
 * Rolling-digit ticker.
 *
 * Accepts either a `value` (formatted here) or an already-formatted `text` —
 * the trading screens format prices through `utils/formatter`, which sometimes
 * returns JSX for subscript-zero notation. A non-string `text` is rendered
 * untouched rather than mangled into digit slots.
 *
 * @param {number} [value] Raw number to format and roll.
 * @param {*} [text] Pre-formatted value. Strings roll; anything else renders as-is.
 * @param {number} [pad] Digits to pad to (left).
 * @param {number} [duration=0.9] Per-digit roll duration, seconds.
 * @param {number} [stagger=0.04] Stagger between digits, seconds.
 * @param {boolean} [startOnView=true] Hold the entry animation until visible.
 * @param {string} [prefix]
 * @param {string} [suffix]
 * @param {boolean} [blur=false] Small blur while digits are in flight.
 * @param {boolean} [locale] Format with locale separators.
 * @param {(value:number)=>string} [format] Custom formatter.
 */
export const NumberTicker = ({
  value,
  text,
  pad,
  duration = 0.9,
  stagger = 0.04,
  startOnView = true,
  prefix,
  suffix,
  blur = false,
  className,
  digitClassName,
  locale,
  format,
  ...rest
}) => {
  const hostRef = useRef(null);
  const inView = useInView(hostRef, startOnView);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!inView || entered) return undefined;
    // Entry runs once; later updates roll rather than re-enter.
    const id = setTimeout(() => setEntered(true), 600);
    return () => clearTimeout(id);
  }, [inView, entered]);

  const body = useMemo(() => {
    if (text !== undefined && text !== null) return text;
    if (value === undefined || value === null || Number.isNaN(Number(value))) return "";
    const n = Number(value);
    if (typeof format === "function") return format(n);
    if (locale) return n.toLocaleString();
    return String(n);
  }, [text, value, format, locale]);

  // Anything that is not a plain string (subscript JSX, an element) is passed
  // straight through — there is nothing safe to slice into digits.
  if (typeof body !== "string") {
    return (
      <span ref={hostRef} className={`ce-num${className ? ` ${className}` : ""}`} {...rest}>
        {prefix}
        {body}
        {suffix}
      </span>
    );
  }

  let chars = body;
  if (pad) {
    const negative = chars.startsWith("-");
    const digitsOnly = negative ? chars.slice(1) : chars;
    if (digitsOnly.length < pad) {
      chars = `${negative ? "-" : ""}${digitsOnly.padStart(pad, "0")}`;
    }
  }

  const full = `${prefix || ""}${chars}${suffix || ""}`;
  const entering = inView && !entered;
  let digitIndex = 0;

  return (
    <span ref={hostRef} className={`ce-num${className ? ` ${className}` : ""}`} {...rest}>
      {inView
        ? Array.from(full).map((ch, i) => {
            if (ch >= "0" && ch <= "9") {
              const delay = digitIndex * stagger;
              digitIndex += 1;
              return (
                <Digit
                  // Position-keyed: a digit that keeps its slot rolls in place
                  // instead of remounting when the value changes.
                  key={`d-${i}`}
                  digit={Number(ch)}
                  duration={duration}
                  delay={delay}
                  blur={blur}
                  className={digitClassName}
                  entering={entering}
                />
              );
            }
            return (
              <span key={`s-${i}`} className="ce-num-static">
                {ch === " " ? " " : ch}
              </span>
            );
          })
        : // Reserve the space before entry so nothing reflows on reveal.
          <span style={{ visibility: "hidden" }}>{full}</span>}
    </span>
  );
};

/**
 * Count-up. Eases from the previously shown value to the new one, so a figure
 * that jumps on a leverage or deposit change travels there instead of cutting.
 *
 * @param {number} value
 * @param {number} [duration=1.2] Seconds.
 * @param {(n:number)=>string} [format]
 * @param {boolean} [startOnView=true]
 */
export const AnimatedNumber = ({
  value,
  duration = 1.2,
  format = (n) => Math.round(n).toLocaleString(),
  className,
  startOnView = true,
  ...rest
}) => {
  const hostRef = useRef(null);
  const inView = useInView(hostRef, startOnView);
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(startOnView ? 0 : target);
  const fromRef = useRef(startOnView ? 0 : target);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!inView) return undefined;
    const from = fromRef.current;
    if (from === target || prefersReducedMotion() || duration <= 0) {
      fromRef.current = target;
      setDisplay(target);
      return undefined;
    }

    const start = performance.now();
    const ms = duration * 1000;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      // Ease-out quint: fast off the mark, settles rather than stops.
      const eased = 1 - Math.pow(1 - t, 5);
      const current = from + (target - from) * eased;
      fromRef.current = current;
      setDisplay(current);
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration, inView]);

  return (
    <span ref={hostRef} className={`ce-num${className ? ` ${className}` : ""}`} {...rest}>
      {format(display)}
    </span>
  );
};

export default NumberTicker;
